/* ============================================================================
 * assets/bundle-upsell-surface.js - Bundle upsell ACCEPT for PDP + homepage_strip
 * (upsell subscribe toggle, 2026-08-28).
 * ----------------------------------------------------------------------------
 * WHAT:  The FIRST working accept on the two on-page upsell surfaces. The
 *        grounded-ritual skin renders a `<button data-skin-cta>` on the PDP
 *        (sections/bundle-upsell-block.liquid) and the homepage strip
 *        (sections/bundle-offer-homepage-strip.liquid), and until now NO JS was
 *        bound to it - the CTA was a dead button. This file binds it.
 *
 *        It is DELIBERATELY NOT the cart file (assets/bundle-upsell.js). That
 *        file is a cart-only custom element (<bundle-upsell-card>) with its own
 *        swap/remove/kill-check protocol; the two on-page surfaces render the
 *        skin DIRECTLY, so they need a small, dedicated accept that only ADDS.
 *        Nothing here touches the cart surface.
 *
 * TWO MODES, one CTA (both fire from the same [data-skin-cta] button):
 *   ONE-TIME (default, every offer): POST /cart/add.js {items:[{id, quantity:1}]}
 *     with the bundle variant id, then open the site cart drawer. No swap, no
 *     remove - PDP/homepage accept just ADDS the bundle.
 *   SUBSCRIBE (sub-eligible offers only): the proven Plan-A component-subscription
 *     flow ported from snippets/bundle-subscribe-toggle.liquid::subscribeCheckout -
 *       1) POST /cart/add.js {items:[{id: componentVariant, quantity: qty,
 *          selling_plan: <plan>}, ...]}
 *       2) POST /cart/update.js {attributes:{bundle_id, bundle_src}}  (add.js
 *          cannot set cart attributes)
 *       3) window.location.href = '/discount/<code>?redirect=/cart'  (a hard
 *          navigation - the discount cookie must be set by the FINAL page load)
 *     Sub-eligible-but-malformed cfg falls back to one-time (never a broken sub).
 *
 * DRAWER OPEN (one-time only): the theme's own add-to-cart (assets/product-form.js)
 *   opens the cart the site way by (a) publishing the theme cartUpdate pubsub -
 *   the signal the native cart-drawer's CartDrawerItems AND the live Rebuy Smart
 *   Cart both listen to - and (b) calling <cart-drawer>.renderContents(response),
 *   which refreshes and OPENS the native drawer. We mirror both. If no <cart-drawer>
 *   element is present we fall back to window.location='/cart'.
 *
 * SURFACE LABEL: the grounded-ritual skin hardcodes data-surface="pdp" on its card
 *   root, so it cannot be trusted on the homepage mount. We derive the surface from
 *   the enclosing SECTION (data-lp-module) instead.
 *
 * GA4: reuses the emit helper the sections already use (window.OMBundleUpsell.emitGA4
 *   when the cart file is loaded, else window.dataLayer.push - the exact pattern in
 *   the sections' inline impression scripts). Emits bundle_upsell_accept with the
 *   five registered params (upsell_surface, upsell_offer_type, upsell_trigger_product,
 *   upsell_variant, bundle_id) PLUS upsell_purchase_type (otp | subscribe) and the
 *   analysis-only upsell_cta_variant (add | subscribe). No capsule/serving counts,
 *   savings as money only, ZERO U+2014 em dashes.
 *
 * IDEMPOTENT + theme-editor safe: per-button + per-container bind guards, and a
 * re-bind on shopify:section:load. A double asset include is a no-op.
 * ========================================================================== */

(function () {
  "use strict";

  // ---- Pure helpers (exported for the headless selftest; no DOM here) --------
  function gidNum(gid) { return String(gid == null ? "" : gid).split("/").pop(); }

  // A decoded sub_config is usable only with the four fields the subscribe flow
  // needs. Missing/empty => the caller falls back to a one-time add.
  function subCfgUsable(cfg) {
    return !!(cfg && cfg.components && cfg.components.length &&
              cfg.selling_plan_id && cfg.code);
  }

  // Build the /cart/add.js line items for the subscribe flow: each component
  // variant on the selling plan, qty multiplied through (upsell qty is always 1).
  function buildSubscribeItems(cfg) {
    var planId = parseInt(cfg.selling_plan_id, 10);
    return (cfg.components || []).map(function (c) {
      return { id: parseInt(gidNum(c.variant_id), 10), quantity: (c.qty || 1), selling_plan: planId };
    });
  }

  function discountUrl(cfg) {
    return "/discount/" + encodeURIComponent(cfg.code) + "?redirect=/cart";
  }

  function acceptPayload(bundleId, surface, purchaseType) {
    return {
      event: "bundle_upsell_accept",
      upsell_surface: surface,
      upsell_offer_type: "bundle_completion",
      upsell_trigger_product: "",
      upsell_variant: "grounded-ritual",             // the cast skin (serve fact)
      upsell_cta_variant: (purchaseType === "subscribe") ? "subscribe" : "add",
      upsell_purchase_type: purchaseType,            // otp | subscribe (new dim)
      bundle_id: bundleId || "",
    };
  }

  // ---- FIRST-PARTY MEASUREMENT BEACON (wi-2026-0784) -------------------------
  // The store is GTM-less, so the dataLayer.push above reaches nothing. This is
  // the direct client->our-own-endpoint path: on impression / accept / dismiss
  // the DOM layer builds this payload and fires it with navigator.sendBeacon
  // (fetch keepalive fallback) to the Fly log host, which appends it verbatim to
  // a queryable fact table. The VERBATIM copy the shopper saw rides along so the
  // operator can QA the language and a future A/B loop can score take-rate by
  // copy variant. These builders are PURE (every input injected) so the headless
  // selftest can assert them with no DOM.

  // A shown money string ("$59.99") -> integer cents (5999), or null. The otp
  // price is only ever a rendered string on the card; the sub price arrives as
  // real cents from sub_config, so only the otp side needs parsing.
  function parseMoneyCents(s) {
    if (s == null) return null;
    var digits = String(s).replace(/[^0-9.]/g, "");
    if (!digits) return null;
    var f = parseFloat(digits);
    if (isNaN(f)) return null;
    return Math.round(f * 100);
  }

  // The four verbatim copy strings the skin stamped into data attributes.
  function buildCopy(get) {
    return {
      reason: get("data-copy-reason") || "",
      savings: get("data-copy-savings") || "",
      cta: get("data-copy-cta") || "",
      heading: get("data-copy-heading") || "",
    };
  }

  // The displayed prices: otp parsed from its shown string, sub taken as real
  // cents (with the shown strings kept for QA).
  function buildPrice(get) {
    var otpDisplay = get("data-price-otp") || "";
    var subDisplay = get("data-price-sub") || "";
    var subRaw = get("data-price-sub-cents");
    var subCents = (subRaw != null && subRaw !== "") ? parseInt(subRaw, 10) : null;
    if (typeof subCents === "number" && isNaN(subCents)) subCents = null;
    return {
      otp_cents: parseMoneyCents(otpDisplay),
      sub_cents: subCents,
      otp_display: otpDisplay || null,
      sub_display: subDisplay || null,
    };
  }

  // The full beacon payload. `o.get(name)` reads a card data attribute; every
  // other field (surface, purchaseType, sessionKey, pageUrl, preview, now) is
  // injected so this stays pure and unit-testable.
  function buildBeaconPayload(o) {
    var get = o.get;
    return {
      event: o.event,                                // impression | accept | dismiss
      ts: o.now,
      surface: o.surface,
      bundle_id: get("data-bundle-id") || "",
      bundle_product_id: get("data-bundle-product-id") || "",
      skin_variant: get("data-skin") || "",
      purchase_type: o.purchaseType || null,         // otp | subscribe | null
      offer_type: get("data-offer-type") || "",
      trigger_product: get("data-trigger-product") || "",
      copy: buildCopy(get),
      price: buildPrice(get),
      session_key: o.sessionKey || "",
      page_url: o.pageUrl || "",
      preview: !!o.preview,
    };
  }

  // POST JSON to a Shopify cart endpoint; reject on any non-200 so the caller's
  // catch can recover. SHARED by the on-page card accept AND the post-purchase
  // param-start landing so the Plan-A network sequence lives in exactly one place.
  // Safe to DEFINE under Node (never called there); fetch is only touched at call.
  function jsonPost(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body),
    }).then(function (r) {
      if (r.status !== 200) throw new Error(url + " -> " + r.status);
      return r.json().catch(function () { return {}; });
    });
  }

  // THE PLAN-A SUBSCRIBE SEQUENCE, single-sourced. Given a usable sub_config cfg:
  //   1) POST /cart/add.js      - every component variant on the selling plan
  //   2) POST /cart/update.js   - bundle_id + bundle_src attributes (add.js can't)
  //   3) hard-navigate /discount/<code>?redirect=/cart (cookie set by final load)
  // opts.onUnusable    cfg not sub-usable -> caller decides the fallback
  // opts.beforeRedirect fires just before the navigation (e.g. a GA4 emit)
  // opts.onError       a failed add/update (non-destructive; nothing checked out)
  // opts.bundleSrc     the cart-attribute source tag (defaults to the upsell tag)
  function subscribeFlow(cfg, opts) {
    opts = opts || {};
    if (!subCfgUsable(cfg)) { if (opts.onUnusable) opts.onUnusable(); return; }
    var items = buildSubscribeItems(cfg);
    jsonPost("/cart/add.js", { items: items })
      .then(function () {
        return jsonPost("/cart/update.js", {
          attributes: { bundle_id: cfg.bundle_id,
                        bundle_src: opts.bundleSrc || "bundle_upsell_subscribe" },
        });
      })
      .then(function () {
        if (opts.beforeRedirect) opts.beforeRedirect();
        window.location.href = discountUrl(cfg);   // hard nav LAST
      })
      .catch(function () { if (opts.onError) opts.onError(); });
  }

  // ---- DOM wiring (guarded so the file is require()-able under Node) ----------
  if (typeof document !== "undefined" && typeof window !== "undefined") {
    if (!window.__omUpsellSurfaceInit) {
      window.__omUpsellSurfaceInit = true;

      // Share the Plan-A machinery on a browser namespace so any other script can
      // drive it WITHOUT forking the sequence (extract-and-share, not duplicate).
      window.OMBundleUpsellSurface = window.OMBundleUpsellSurface || {
        gidNum: gidNum, subCfgUsable: subCfgUsable,
        buildSubscribeItems: buildSubscribeItems, discountUrl: discountUrl,
        acceptPayload: acceptPayload, subscribeFlow: subscribeFlow,
        parseMoneyCents: parseMoneyCents, buildCopy: buildCopy,
        buildPrice: buildPrice, buildBeaconPayload: buildBeaconPayload,
      };

      var BOUND = "__omSurfaceBound";
      var SECTION_SELECTOR =
        '[data-lp-module="bundle-upsell-block"], [data-lp-module="bundle-offer-homepage-strip"]';

      function decodeJson(raw) {
        if (!raw) return null;
        try {
          var ta = document.createElement("textarea");
          ta.innerHTML = raw;            // decode HTML entities the attr was escaped with
          return JSON.parse(ta.value);
        } catch (e) { return null; }
      }

      // Surface from the enclosing section, never the skin's hardcoded data-surface.
      function surfaceOf(card) {
        if (card.closest('[data-lp-module="bundle-offer-homepage-strip"]')) return "homepage_strip";
        return "pdp";
      }

      function emitGA4(payload) {
        try {
          if (window.OMBundleUpsell && typeof window.OMBundleUpsell.emitGA4 === "function") {
            window.OMBundleUpsell.emitGA4(payload);
            return;
          }
        } catch (e) { /* fall through */ }
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push(payload);
      }

      // ---- FIRST-PARTY MEASUREMENT BEACON — DOM side (wi-2026-0784) ----------
      // The endpoint is overridable (staging vs a future host move) via a global,
      // else the live Fly log host. The host is INVISIBLE to shoppers: it only
      // receives telemetry, so it may run while the upsell surfaces are off.
      var LOG_ENDPOINT = window.OM_UPSELL_LOG_ENDPOINT || "https://om-upsell-log.fly.dev/log";

      // A per-viewer id, persisted for the session so an impression and its later
      // accept/dismiss share a key. sessionStorage can throw (private mode) or be
      // blocked; fall back to a per-pageview in-memory id rather than crash.
      function sessionKey() {
        var K = "om_upsell_sk";
        try {
          var v = window.sessionStorage.getItem(K);
          if (!v) {
            v = "sk-" + Date.now().toString(36) + "-" + Math.random().toString(36).slice(2, 10);
            window.sessionStorage.setItem(K, v);
          }
          return v;
        } catch (e) {
          if (!window.__omUpsellSK) {
            window.__omUpsellSK = "sk-mem-" + Math.random().toString(36).slice(2, 12);
          }
          return window.__omUpsellSK;
        }
      }

      // preview = not a real shopper on the live theme: the theme customizer
      // (Shopify.designMode) or any non-published theme role (development /
      // unpublished — `shopify theme dev` and the staging theme). On the live
      // theme with a real shopper, role === 'main' and this is false, so the QA
      // log can filter to real shoppers with `WHERE preview = false`.
      function isPreview() {
        try {
          if (window.Shopify && window.Shopify.designMode) return true;
          if (window.Shopify && window.Shopify.theme && window.Shopify.theme.role &&
              String(window.Shopify.theme.role).toLowerCase() !== "main") return true;
        } catch (e) { /* fall through */ }
        return false;
      }

      // Fire-and-forget. sendBeacon survives an unloading page (the accept/dismiss
      // case); a text/plain body keeps it a CORS "simple request" (no preflight),
      // and the server parses JSON regardless of content-type. fetch keepalive is
      // the fallback where sendBeacon is unavailable or refuses. NEVER blocks or
      // throws into the UI.
      function sendBeacon(payload) {
        var body;
        try { body = JSON.stringify(payload); } catch (e) { return; }
        try {
          if (navigator && typeof navigator.sendBeacon === "function") {
            var blob = new Blob([body], { type: "text/plain;charset=UTF-8" });
            if (navigator.sendBeacon(LOG_ENDPOINT, blob)) return;
          }
        } catch (e) { /* fall through to fetch */ }
        try {
          fetch(LOG_ENDPOINT, {
            method: "POST",
            headers: { "Content-Type": "text/plain;charset=UTF-8" },
            body: body, keepalive: true, mode: "cors", credentials: "omit",
          }).catch(function () {});
        } catch (e) { /* fire-and-forget */ }
      }

      function getAttrOf(card) { return function (name) { return card.getAttribute(name); }; }

      function emitBeacon(eventName, card, purchaseType) {
        try {
          sendBeacon(buildBeaconPayload({
            event: eventName, now: Date.now(), surface: surfaceOf(card),
            get: getAttrOf(card), purchaseType: purchaseType,
            sessionKey: sessionKey(), pageUrl: (window.location && window.location.href) || "",
            preview: isPreview(),
          }));
        } catch (e) { /* measurement must never break the surface */ }
      }

      // Impression: fire once per (surface|bundle|skin) per pageview, when the
      // card is actually in view (an off-screen render below the fold is not an
      // impression). Deduped by key so a theme-editor re-render or a double asset
      // include cannot double-count.
      var _imprSeen = {};
      function impressionKey(card) {
        return surfaceOf(card) + "|" + (card.getAttribute("data-bundle-id") || "") +
               "|" + (card.getAttribute("data-skin") || "");
      }
      var _imprObserver = null;
      function ensureObserver() {
        if (_imprObserver || typeof IntersectionObserver === "undefined") return _imprObserver;
        _imprObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting || entry.intersectionRatio < 0.3) return;
            var card = entry.target.__omImprCard || entry.target;
            _imprObserver.unobserve(entry.target);
            var key = impressionKey(card);
            if (_imprSeen[key]) return;
            _imprSeen[key] = true;
            emitBeacon("impression", card, null);
          });
        }, { threshold: [0, 0.3, 0.6] });
        return _imprObserver;
      }
      // A card root styled display:contents (the homepage strip's .om.d1) has no
      // box of its own, so IntersectionObserver never reports it intersecting
      // and its impression never fires (wi-2026-1238: zero homepage_strip
      // impressions after the 2026-10-01 flip while accepts kept logging).
      // Observe its first rendered descendant instead; the callback maps the
      // target back to the card, so the payload is still read off the card.
      function observeTarget(card) {
        var cs = window.getComputedStyle ? window.getComputedStyle(card) : null;
        if (!cs || cs.display !== "contents") return card;
        var el = card.firstElementChild;
        while (el) {
          var d = window.getComputedStyle(el).display;
          if (d === "contents") { el = el.firstElementChild || el.nextElementSibling; continue; }
          if (d !== "none") return el;
          el = el.nextElementSibling;
        }
        return card;
      }
      function observeCard(card) {
        if (!card || card.__omImprObserved) return;
        card.__omImprObserved = true;
        var obs = ensureObserver();
        if (obs) {
          var target = observeTarget(card);
          target.__omImprCard = card;
          obs.observe(target);
          return;
        }
        // No IntersectionObserver (very old browser): count the render itself.
        var key = impressionKey(card);
        if (!_imprSeen[key]) { _imprSeen[key] = true; emitBeacon("impression", card, null); }
      }

      // Dismiss: no on-page surface renders a dismiss control today, so this is a
      // future-proof hook — any [data-skin-dismiss] element on a card fires a
      // dismiss beacon. Bound the same idempotent way as the CTA.
      function onDismissClick(e) {
        var btn = e.currentTarget;
        var card = btn.closest("[data-bundle-variant-id]") || btn.closest('[data-skin]');
        if (!card) return;
        emitBeacon("dismiss", card, null);
      }

      // The theme's own drawer-open path (product-form.js): publish the cartUpdate
      // pubsub (native CartDrawerItems + Rebuy Smart Cart both listen) AND call the
      // native cart-drawer renderContents (which opens it). Returns true if a drawer
      // path ran; false => caller navigates to /cart.
      function openCartDrawer(addResp, variantId) {
        try {
          if (typeof window.publish === "function" &&
              typeof window.PUB_SUB_EVENTS !== "undefined" &&
              window.PUB_SUB_EVENTS.cartUpdate) {
            window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
              source: "bundle-upsell-surface",
              productVariantId: variantId,
              cartData: addResp,
            });
          }
        } catch (e) { /* pubsub optional */ }
        try {
          var drawer = document.querySelector("cart-drawer");
          if (drawer && typeof drawer.renderContents === "function") {
            drawer.renderContents(addResp);
            return true;
          }
        } catch (e) { /* fall through to /cart */ }
        return false;
      }

      function currentMode(card) {
        if (card.getAttribute("data-sub-eligible") !== "true") return "onetime";
        var checked = card.querySelector('input[name^="d1-mode-"]:checked');
        return checked ? checked.value : "onetime";
      }

      function done(card, btn) {
        card.__omAccepting = false;
        if (btn) btn.removeAttribute("aria-busy");
      }

      function acceptOneTime(card, btn, surface) {
        var vid = parseInt(card.getAttribute("data-bundle-variant-id"), 10);
        if (!vid) { done(card, btn); return; }
        jsonPost("/cart/add.js", { items: [{ id: vid, quantity: 1 }] })
          .then(function (resp) {
            emitGA4(acceptPayload(card.getAttribute("data-bundle-id"), surface, "otp"));
            emitBeacon("accept", card, "otp");     // first-party measurement (wi-2026-0784)
            var opened = openCartDrawer(resp, vid);
            done(card, btn);
            if (!opened) window.location.href = "/cart";
          })
          .catch(function () {
            done(card, btn);
            window.location.href = "/cart";  // add may have failed; /cart is honest
          });
      }

      function acceptSubscribe(card, btn, surface) {
        var cfg = decodeJson(card.getAttribute("data-sub-config"));
        // The Plan-A sequence is shared with the param-start landing (subscribeFlow).
        // Eligible-but-malformed cfg => fall back to one-time; success emits GA4 just
        // before the hard navigation; a failed add/update re-enables the CTA.
        subscribeFlow(cfg, {
          bundleSrc: "bundle_upsell_subscribe",
          onUnusable: function () { acceptOneTime(card, btn, surface); },
          beforeRedirect: function () {
            emitGA4(acceptPayload(card.getAttribute("data-bundle-id"), surface, "subscribe"));
            emitBeacon("accept", card, "subscribe");   // first-party measurement (wi-2026-0784)
          },
          onError: function () { done(card, btn); },
        });
      }

      function onCtaClick(e) {
        var btn = e.currentTarget;
        var card = btn.closest("[data-bundle-variant-id]") ||
                   btn.closest('[data-skin="grounded-ritual"]');
        if (!card) return;                 // a skin without our data => leave native
        e.preventDefault();
        if (card.__omAccepting) return;    // per-card click lock (double-fire guard)
        card.__omAccepting = true;
        if (btn) btn.setAttribute("aria-busy", "true");
        var surface = surfaceOf(card);
        if (currentMode(card) === "subscribe") acceptSubscribe(card, btn, surface);
        else acceptOneTime(card, btn, surface);
      }

      // Swap the "You save $X vs buying separately" amount to match the selected
      // purchase mode. The skin renders the ONE-TIME amount server-side (data-save-otp)
      // and the SUBSCRIBE-basis amount (data-save-sub) only when it is a positive
      // saving. Subscribe with no positive sub saving HIDES the line (mirrors the PDP
      // callout's PATH C), never shows the stale one-time figure. Presentation only;
      // the accept flow reads prices from sub_config, not this text.
      function updateSaveLine(card, mode) {
        if (!card) return;
        var saveEl = card.querySelector("[data-skin-save]");
        if (!saveEl) return;                 // older markup with no swappable amount
        var amtEl = saveEl.querySelector("[data-skin-save-amt]");
        var otp = saveEl.getAttribute("data-save-otp");
        var sub = saveEl.getAttribute("data-save-sub");
        if (mode === "subscribe") {
          if (sub) {
            if (amtEl) amtEl.textContent = sub;
            saveEl.style.display = "";
          } else {
            saveEl.style.display = "none";   // no honest positive sub saving -> hide
          }
        } else {
          if (amtEl && otp) amtEl.textContent = otp;
          saveEl.style.display = "";
        }
        // Keep the measurement stamp equal to what the shopper now sees
        // (wi-2026-0784 contract), hidden line => empty.
        try {
          card.setAttribute("data-copy-savings",
            saveEl.style.display === "none" ? "" : saveEl.textContent.trim());
        } catch (e) { /* measurement only */ }
      }

      // Swap the dollar figure inside the REASON line ("... and save $9.99. ...")
      // to match the selected mode (wi-2026-1301). The skin renders the amount
      // in [data-skin-why-amt] with data-why-otp / data-why-sub. Unlike the
      // savings line the reason is never hidden: with no positive subscribe
      // saving it keeps the one-time figure, which is still a true sentence.
      function updateWhyLine(card, mode) {
        if (!card) return;
        var whyEl = card.querySelector("[data-skin-why]");
        if (!whyEl) return;                  // reason without a swappable amount
        var amtEl = whyEl.querySelector("[data-skin-why-amt]");
        var otp = whyEl.getAttribute("data-why-otp");
        var sub = whyEl.getAttribute("data-why-sub");
        var amt = (mode === "subscribe" && sub) ? sub : otp;
        if (amtEl && amt) amtEl.textContent = amt;
        try { card.setAttribute("data-copy-reason", whyEl.textContent.trim()); } catch (e) { /* measurement only */ }
      }

      // ---- HOMEPAGE STRIP: the button carries the selected option ------------
      // (home launch T2 round 2, 2026-09-29). ADDITIVE and strip-only: the
      // skin renders [data-cta-label] / [data-cta-sub] spans and the
      // data-cta-label-<mode> / data-cta-note-<mode> words ONLY for the
      // homepage strip (card_layout: 'strip_sub_first'). On every other card
      // the label span does not exist and this returns at once, so PDP, cart
      // and routine-board behaviour is unchanged. Every word and price is read
      // from attributes the server rendered from the section settings and the
      // engine's own values; nothing is hardcoded here. The button's text IS
      // its accessible name, so swapping the spans updates the name too.
      // data-copy-cta is kept equal to what the shopper sees, so the
      // measurement beacon logs the real button text on this surface.
      function updateCtaLabel(card, mode) {
        if (!card) return;
        var labelEl = card.querySelector("[data-cta-label]");
        if (!labelEl) return;                // not the strip layout: no-op
        var btn = labelEl.closest("[data-skin-cta]");
        if (!btn) return;
        var key = (mode === "subscribe") ? "subscribe" : "onetime";
        var label = btn.getAttribute("data-cta-label-" + key);
        var note = btn.getAttribute("data-cta-note-" + key);
        if (label == null) return;           // no words for this mode: leave as rendered
        labelEl.textContent = label;
        var noteEl = btn.querySelector("[data-cta-sub]");
        if (noteEl) noteEl.textContent = note || "";
        try { card.setAttribute("data-copy-cta", (label + " " + (note || "")).trim()); } catch (e) { /* measurement only */ }
      }

      // Bring a card fully in line with its CHECKED radio (the browser can
      // restore a different radio on back/forward navigation than the one the
      // server marked). Every card with a purchase toggle since Subscribe became
      // the default everywhere (wi-2026-1301); was strip-only. The CTA label swap
      // inside still no-ops off the strip.
      function syncStripCard(card) {
        if (!card || !card.querySelector("[data-d1-buymode]")) return;
        var mode = currentMode(card);
        card.querySelectorAll("[data-d1-mode]").forEach(function (el) {
          el.classList.toggle("is-selected", el.getAttribute("data-d1-mode") === mode);
        });
        updateSaveLine(card, mode);
        updateWhyLine(card, mode);
        updateCtaLabel(card, mode);
      }

      // Toggle selection styling (presentation only; the accept reads the radio).
      function onModeChange(e) {
        var input = e.target;
        if (!input || String(input.name || "").indexOf("d1-mode-") !== 0) return;
        var group = input.closest("[data-d1-buymode]");
        if (!group) return;
        group.querySelectorAll("[data-d1-mode]").forEach(function (el) {
          el.classList.toggle("is-selected", el.getAttribute("data-d1-mode") === input.value);
        });
        var card = input.closest('[data-skin="grounded-ritual"]') ||
                   input.closest("[data-bundle-variant-id]");
        updateSaveLine(card, input.value);
        updateWhyLine(card, input.value);
        updateCtaLabel(card, input.value);   // strip only; a no-op elsewhere
      }

      function bind(root) {
        var scope = (root && root.querySelectorAll) ? root : document;
        var containers = scope.querySelectorAll(SECTION_SELECTOR);
        // shopify:section:load hands us the section itself, which may BE a container.
        var list = Array.prototype.slice.call(containers);
        if (root && root.matches && root.matches(SECTION_SELECTOR)) list.push(root);
        list.forEach(function (container) {
          container.querySelectorAll("[data-skin-cta]").forEach(function (btn) {
            if (btn[BOUND]) return;
            btn[BOUND] = true;
            btn.addEventListener("click", onCtaClick);
          });
          // Impression + dismiss instrumentation (wi-2026-0784). Observe every
          // card root for the in-view impression; bind any dismiss control.
          container.querySelectorAll("[data-bundle-variant-id][data-skin]").forEach(function (card) {
            syncStripCard(card);             // any card with a toggle; before the impression
            observeCard(card);
          });
          container.querySelectorAll("[data-skin-dismiss]").forEach(function (btn) {
            if (btn.__omDismissBound) return;
            btn.__omDismissBound = true;
            btn.addEventListener("click", onDismissClick);
          });
          if (!container[BOUND]) {
            container[BOUND] = true;
            container.addEventListener("change", onModeChange);
          }
        });
      }

      // ---- POST-PURCHASE PARAM-START LANDING -------------------------------
      // A shopper arrives from the Thank-You extension at any theme URL carrying
      // ?start_bundle_sub=<bundle_product_gid|numeric_id>. This runs the SAME
      // Plan-A subscribeFlow the on-page card uses, then the discount redirect
      // sends them to /cart to complete the subscription. Needs NO store page:
      // the per-bundle sub_config is rendered globally into #om-bundle-sub-config
      // by snippets/bundle-subscribe-start.liquid (in the layout). Absent param =>
      // a normal page load, nothing happens. Missing/ineligible/malformed cfg =>
      // a graceful /cart redirect, never an error dump. No U+2014 em dashes.

      // The param value may be the full product GID (URL-encoded) or a bare
      // numeric id; either way the sub_config map is keyed by the numeric id, so
      // reduce to the trailing id.
      function startBundleSubId() {
        var m = /[?&]start_bundle_sub=([^&#]+)/.exec(window.location.search || "");
        if (!m) return null;
        var raw;
        try { raw = decodeURIComponent(m[1]); } catch (e) { raw = m[1]; }
        raw = String(raw).replace(/\/+$/, "");   // tolerate a trailing slash on a GID
        return raw.split("/").pop();             // GID tail, or the bare numeric id
      }

      function readSubConfigMap() {
        var el = document.getElementById("om-bundle-sub-config");
        if (!el) return null;
        try { return JSON.parse(el.textContent || "{}"); } catch (e) { return null; }
      }

      // A minimal, brand-token-bound full-screen "setting up" state. Beige field,
      // forest ink + spinner, with literal fallbacks so it renders even before the
      // theme's :root vars resolve. Presentation only; the redirect follows.
      function showSubStartOverlay() {
        if (document.getElementById("om-sub-start-overlay")) return;
        var o = document.createElement("div");
        o.id = "om-sub-start-overlay";
        o.setAttribute("role", "status");
        o.setAttribute("aria-live", "polite");
        o.style.cssText = [
          "position:fixed", "inset:0", "z-index:2147483000",
          "display:flex", "flex-direction:column", "align-items:center",
          "justify-content:center", "gap:18px", "text-align:center", "padding:24px",
          "background:var(--color-primary-beige,#F1E7DB)",
          "color:var(--color-primary-forest,#284734)",
          "font-family:var(--bst-font-primary,-apple-system,BlinkMacSystemFont,'Segoe UI',Helvetica,Arial,sans-serif)"
        ].join(";");
        var spin = document.createElement("div");
        spin.style.cssText = [
          "width:34px", "height:34px", "border-radius:50%",
          "border:3px solid rgba(40,71,52,0.2)",
          "border-top-color:var(--color-primary-forest,#284734)",
          "animation:om-sub-spin 0.8s linear infinite"
        ].join(";");
        var msg = document.createElement("p");
        msg.textContent = "Setting up your subscription…";  // ellipsis, not em dash
        msg.style.cssText = "margin:0;font-size:18px;font-weight:600;letter-spacing:.01em;";
        var kf = document.createElement("style");
        kf.textContent = "@keyframes om-sub-spin{to{transform:rotate(360deg)}}";
        o.appendChild(kf); o.appendChild(spin); o.appendChild(msg);
        (document.body || document.documentElement).appendChild(o);
      }

      function initSubscribeStart() {
        var id = startBundleSubId();
        if (!id) return;                         // no param => ordinary page load
        var map = readSubConfigMap() || {};
        var cfg = map[id] || null;
        // Missing bundle, not sub-eligible, or malformed cfg => graceful /cart.
        if (!subCfgUsable(cfg)) { window.location.href = "/cart"; return; }
        showSubStartOverlay();
        subscribeFlow(cfg, {
          bundleSrc: "bundle_post_purchase_subscribe",
          onError: function () { window.location.href = "/cart"; },
        });
      }

      function init() { bind(document); initSubscribeStart(); }
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", init, { once: true });
      } else {
        init();
      }
      document.addEventListener("shopify:section:load", function (e) {
        bind(e.target || document);
      });
    }
  }

  // ---- Node export for the headless selftest (bundle-upsell-surface.selftest.js)
  if (typeof module !== "undefined" && module.exports) {
    module.exports = { gidNum: gidNum, subCfgUsable: subCfgUsable,
                       buildSubscribeItems: buildSubscribeItems,
                       discountUrl: discountUrl, acceptPayload: acceptPayload,
                       subscribeFlow: subscribeFlow,
                       parseMoneyCents: parseMoneyCents, buildCopy: buildCopy,
                       buildPrice: buildPrice, buildBeaconPayload: buildBeaconPayload };
  }
})();
